import axios from 'axios';
import { STORAGE_KEYS } from '../constants/storage-keys';
import { notifyAuthTokenChanged, notifyAuthUserChanged } from '../constants/auth-events';
import { prepareApiError } from '../utils/apiError';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
export const FIRM_ID = process.env.REACT_APP_FIRM_ID || '';

export const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    ...(FIRM_ID ? { 'x-firm-id': FIRM_ID } : {}),
  },
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
  if (token) {
    config.headers = config.headers ?? {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  // Default Content-Type is application/json; that breaks multipart (multer sees no file).
  // Let the browser set multipart/form-data; boundary=...
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    if (typeof config.headers?.set === 'function') {
      config.headers.set('Content-Type', false as unknown as string);
    } else if (config.headers) {
      delete (config.headers as Record<string, unknown>)['Content-Type'];
      delete (config.headers as Record<string, unknown>)['content-type'];
    }
  }
  return config;
});

let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function onRefreshed(token: string) {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
}

/** Login / MFA 401 must reach the form (error-banner), not reload /login. */
export function isCredentialAuthUrl(url?: string): boolean {
  const u = url ?? '';
  return u.includes('/auth/login') || u.includes('/auth/mfa/verify');
}

api.interceptors.response.use(
  r => r,
  async err => {
    const original = err.config;
    const requestUrl = String(original?.url ?? '');
    // Skip refresh for auth endpoints themselves
    if (err.response?.status === 401 && !original._retry &&
        !isCredentialAuthUrl(requestUrl) &&
        !requestUrl.includes('/auth/refresh')) {
      if (isRefreshing) {
        return new Promise(resolve => {
          refreshSubscribers.push((token: string) => {
            original.headers['Authorization'] = `Bearer ${token}`;
            resolve(api(original));
          });
        });
      }
      original._retry = true;
      isRefreshing = true;
      try {
        const res = await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
        const { access_token, user } = res.data;
        localStorage.setItem(STORAGE_KEYS.TOKEN, access_token);
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
        notifyAuthUserChanged();
        notifyAuthTokenChanged();
        api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        onRefreshed(access_token);
        original.headers['Authorization'] = `Bearer ${access_token}`;
        return api(original);
      } catch {
        // Refresh failed — clear session and redirect to login
        localStorage.removeItem(STORAGE_KEYS.TOKEN);
        localStorage.removeItem(STORAGE_KEYS.USER);
        window.location.href = '/login';
      } finally {
        isRefreshing = false;
      }
    }
    if (err.response?.status === 401 && !isCredentialAuthUrl(requestUrl)) {
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      window.location.href = '/login';
    }
    prepareApiError(err);
    return Promise.reject(err);
  }
);

export default api;
